import { prefetchProductCatalogCache, queryClient } from "api/cached";
import { getApiBuildInfo } from "api/bussiness/build-info/build-info-business";
import { DateTimeProvider } from "commons/ultilities/date-time-provider/date-time-provider";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { SnackbarProvider } from "notistack";
import { DeleteConfirmation } from "pages/common/delete-confimation";
import { ValidationErrorPopup } from "pages/common/validation-error-popup";
import { ChangePw } from "pages/home/profile/change-pw";
import { useEffect, useRef } from "react";
import { RouterProvider } from "react-router-dom";
import { useLocalizedYupSchema } from "./localization/yup-validation";
import { router } from "./router/router";

const toShortHash = (hash?: string) =>
  hash && hash.length > 7 ? hash.slice(0, 7) : hash ?? "—";

const formatBuildDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const App = observer(() => {
  const {
    authentication: { isAuthenticated },
  } = useStores();
  const hasPrefetchedCatalog = useRef(false);
  const hasLoggedBuildInfo = useRef(false);

  useLocalizedYupSchema();

  useEffect(() => {
    if (!isAuthenticated || hasPrefetchedCatalog.current) {
      return;
    }

    hasPrefetchedCatalog.current = true;

    // Delay protected prefetch until authentication is established to avoid 401 logout loops.
    void prefetchProductCatalogCache(queryClient).catch((error: unknown) => {
      console.error("Failed to prefetch product catalog cache", error);
    });
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || hasLoggedBuildInfo.current) {
      return;
    }

    hasLoggedBuildInfo.current = true;

    // Fetch after authentication is established to avoid 401 logout loops.
    void getApiBuildInfo().then((apiInfo) => {
      console.log(
        `UI build: ${toShortHash(__BUILD__.commitHash)} • ${formatBuildDate(__BUILD__.commitDate)}`,
      );
      console.log(
        `API build: ${toShortHash(apiInfo?.gitHash)} • ${formatBuildDate(apiInfo?.buildDate)}`,
      );
    });
  }, [isAuthenticated]);

  return (
    <DateTimeProvider>
      <SnackbarProvider
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        autoHideDuration={2000}
      >
        <RouterProvider router={router} />
        <DeleteConfirmation></DeleteConfirmation>
        <ChangePw></ChangePw>
        <ValidationErrorPopup></ValidationErrorPopup>
      </SnackbarProvider>
    </DateTimeProvider>
  );
});

export default App;
